'use client'

import { C, Callout, Exercise, H, Lesson, P, Quiz, Step, UL } from '../_components/lesson'
import * as EX from './exercises/code-extra'

export default function TextAndCollections() {
  return (
    <Lesson>
      <Step>
        <H>Text is data too</H>
        <P>
          Language models are trained on text, so we need to be good at handling it. A <strong>string</strong> is a sequence of
          characters. You can measure it, grab single characters by position (starting at 0, like arrays), cut slices out of it,
          change its case, search it, and split it into pieces.
        </P>
        <Exercise id="strings" title="The string toolbox" {...EX.strings} />
        <Callout kind="plain">
          Strings never change in place. <C>s.toLowerCase()</C> gives you a <em>new</em> string and leaves <C>s</C> alone — so
          you have to store the result: <C>const lower = s.toLowerCase()</C>.
        </Callout>
      </Step>

      <Step gate={['q-split', 'vowels']}>
        <H>Splitting and joining</H>
        <P>
          The two most useful string methods for us are <C>split</C> (cut a string into an array wherever a separator appears)
          and <C>join</C> (glue an array back into a string). Between them sits everything you know about arrays: map, filter,
          reverse, sort.
        </P>
        <Quiz
          id="q-split"
          question={
            <>
              What is <C>{'"a,b,,c".split(",")'}</C>?
            </>
          }
          options={[
            { label: <C>{'["a", "b", "", "c"]'}</C>, correct: true },
            { label: <C>{'["a", "b", "c"]'}</C>, why: 'Two commas in a row leave an empty string between them — split doesn’t skip it.' },
            { label: <C>{'["a,b,,c"]'}</C>, why: 'That would happen only if the separator never appeared.' },
          ]}
          explain="Empty pieces are a classic source of bugs when splitting real text. Filtering them out with .filter(w => w !== '') is a common fix."
        />
        <Exercise
          id="vowels"
          title="Three string functions"
          {...EX.vowels}
          hints={[
            <>
              Loop with <C>{'for (const ch of s.toLowerCase())'}</C> and test <C>{'"aeiou".includes(ch)'}</C>.
            </>,
            <>
              <C>{'sentence.split(" ").reverse().join(" ")'}</C> reverses the words.
            </>,
            <>
              For each word: <C>{'w.slice(0, 1).toUpperCase() + w.slice(1)'}</C>.
            </>,
          ]}
        />
      </Step>

      <Step>
        <H>Sets and Maps</H>
        <P>Two collections you’ll use constantly when working with text and data:</P>
        <UL>
          <li>
            A <strong>Set</strong> holds each value at most once. Add the same thing twice and it’s still there once — perfect for
            finding the distinct characters or words in a text (the <em>vocabulary</em>).
          </li>
          <li>
            A <strong>Map</strong> stores <em>key → value</em> pairs and looks values up by key instantly. It’s the natural way to
            count things: the key is the thing, the value is how many times you’ve seen it.
          </li>
        </UL>
        <Exercise id="sets-maps" title="Sets, Maps and dictionaries" {...EX.setsMaps} />
        <Callout kind="notation">
          <C>a ?? b</C> means “use <C>a</C>, unless it’s missing (<C>undefined</C> or <C>null</C>), in which case use <C>b</C>”.
          <C> counts.get(ch) ?? 0</C> reads: the count so far, or 0 if we haven’t seen this character yet.
        </Callout>
      </Step>

      <Step gate="word-counts">
        <H>Count words</H>
        <P>
          Counting is the beating heart of language modelling — in Module 8 you’ll build a whole model out of counts. Start here:
        </P>
        <Exercise
          id="word-counts"
          title="wordCounts, mostCommon, vocabulary"
          {...EX.wordCounts}
          hints={[
            <>
              Inside the loop over words: <C>{'counts.set(w, (counts.get(w) ?? 0) + 1)'}</C>. Skip empty words with{' '}
              <C>continue</C>.
            </>,
            <>
              For the vocabulary: <C>{'[...new Set(words)].sort()'}</C> — spreading a Set gives an array.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-map">
        <H>Which collection when?</H>
        <Quiz
          id="q-map"
          question="You want to know, for every character in a book, how many times it appears. Which collection fits best?"
          options={[
            { label: 'A Map from character to count', correct: true },
            { label: 'A Set of characters', why: 'A Set tells you which characters appear, but can’t remember how many times.' },
            { label: 'An array of all the characters', why: 'You could, but counting would mean scanning it again for every character.' },
          ]}
        />
        <Callout kind="why">
          Tokenizers are built from Sets (the vocabulary) and Maps (token → number). Bigram language models are Maps of Maps. You
          now have both tools.
        </Callout>
      </Step>
    </Lesson>
  )
}
